import { useState } from 'react';
import { Info, TrendingUp } from 'lucide-react';

function GaugeArc({ score }) {
  const radius = 70;
  const cx = 90;
  const cy = 90;
  const startAngle = -210;
  const endAngle = 30;
  const totalDeg = endAngle - startAngle;
  const scoreDeg = startAngle + totalDeg * score;

  const toRad = (deg) => (deg * Math.PI) / 180;
  const arcPath = (cx, cy, r, startDeg, endDeg) => {
    const x1 = cx + r * Math.cos(toRad(startDeg));
    const y1 = cy + r * Math.sin(toRad(startDeg));
    const x2 = cx + r * Math.cos(toRad(endDeg));
    const y2 = cy + r * Math.sin(toRad(endDeg));
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };

  const needleX = cx + (radius - 10) * Math.cos(toRad(scoreDeg));
  const needleY = cy + (radius - 10) * Math.sin(toRad(scoreDeg));

  const scoreColor = score >= 0.8 ? '#ef4444' : score >= 0.5 ? '#f59e0b' : '#22c55e';

  return (
    <svg width={180} height={130} viewBox="0 0 180 130">
      {/* Track */}
      <path d={arcPath(cx, cy, radius, startAngle, endAngle)} fill="none" stroke="#e5e7eb" strokeWidth={14} strokeLinecap="round" />
      {/* Green segment */}
      <path d={arcPath(cx, cy, radius, startAngle, startAngle + totalDeg * 0.33)} fill="none" stroke="#22c55e" strokeWidth={14} strokeLinecap="round" opacity={0.6} />
      {/* Yellow segment */}
      <path d={arcPath(cx, cy, radius, startAngle + totalDeg * 0.33, startAngle + totalDeg * 0.66)} fill="none" stroke="#f59e0b" strokeWidth={14} strokeLinecap="round" opacity={0.6} />
      {/* Red segment */}
      <path d={arcPath(cx, cy, radius, startAngle + totalDeg * 0.66, endAngle)} fill="none" stroke="#ef4444" strokeWidth={14} strokeLinecap="round" opacity={0.6} />
      {/* Score arc */}
      <path d={arcPath(cx, cy, radius, startAngle, scoreDeg)} fill="none" stroke={scoreColor} strokeWidth={14} strokeLinecap="round" />
      {/* Needle */}
      <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke={scoreColor} strokeWidth={3} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={6} fill={scoreColor} />
      {/* Score text */}
      <text x={cx} y={cy + 25} textAnchor="middle" fontSize={22} fontWeight="800" fill={scoreColor}>
        {score.toFixed(3)}
      </text>
    </svg>
  );
}

function ComponentBar({ label, value, color }) {
  const pct = Math.round(value * 100);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between text-xs">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="font-bold tabular-nums" style={{ color }}>{pct}%</span>
      </div>
      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

const COMPONENT_COLORS = {
  functional_impact: '#ef4444',
  population_rarity: '#6366f1',
  conservation: '#14b8a6',
  clinical_evidence: '#f93441',
};

const COMPONENT_LABELS = {
  functional_impact: 'Functional Impact',
  population_rarity: 'Population Rarity',
  conservation: 'Conservation',
  clinical_evidence: 'Clinical Evidence',
};

export default function MGSScore({ mgs }) {
  const [showTip, setShowTip] = useState(false);
  const scoreColor = mgs.score >= 0.8 ? '#ef4444' : mgs.score >= 0.5 ? '#f59e0b' : '#22c55e';
  const classColors = {
    'High Risk': { bg: '#fef2f2', text: '#991b1b', border: '#fca5a5' },
    'Moderate Risk': { bg: '#fff7ed', text: '#9a3412', border: '#fdba74' },
    'Low Risk': { bg: '#f0fdf4', text: '#14532d', border: '#86efac' },
  };
  const cc = classColors[mgs.classification] || classColors['Moderate Risk'];

  return (
    <section id="mgs" className="bg-white rounded-2xl shadow-card overflow-hidden">
      <div className="px-6 py-4 border-b flex items-center justify-between" style={{ background: '#021f30' }}>
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-white/70" />
          <h3 className="text-white font-bold text-base">MGS Composite Score</h3>
          <div className="relative">
            <button
              className="ml-1 text-white/40 hover:text-white transition-colors"
              onMouseEnter={() => setShowTip(true)}
              onMouseLeave={() => setShowTip(false)}
            >
              <Info size={14} />
            </button>
            {showTip && (
              <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-xl p-3 z-30 shadow-2xl leading-relaxed">
                The <strong>Multidimensional Genomic Score (MGS)</strong> integrates functional impact,
                population rarity, evolutionary conservation, and clinical evidence into a single
                composite risk score ranging from 0 (benign) to 1 (high pathogenic risk).
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Gauge + badges */}
          <div className="flex flex-col items-center gap-4">
            <GaugeArc score={mgs.score} />

            <div className="flex flex-col items-center gap-2">
              <div
                className="px-5 py-2 rounded-xl border font-bold text-sm"
                style={{ background: cc.bg, color: cc.text, borderColor: cc.border }}
              >
                {mgs.classification}
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="text-center">
                  <div className="text-xs text-gray-500">Percentile</div>
                  <div className="font-extrabold text-xl" style={{ color: scoreColor }}>
                    {mgs.percentile.toFixed(1)}th
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-400 text-center max-w-[200px] leading-relaxed">
                Scored higher than {mgs.percentile.toFixed(0)}% of all variants in the training cohort
              </p>
            </div>
          </div>

          {/* Component bars */}
          <div className="flex flex-col justify-center gap-5">
            <h4 className="text-sm font-semibold text-gray-700">Score Components</h4>
            {Object.entries(mgs.components).map(([key, value]) => (
              <ComponentBar
                key={key}
                label={COMPONENT_LABELS[key] || key}
                value={value}
                color={COMPONENT_COLORS[key] || '#6366f1'}
              />
            ))}

            <div className="mt-2 p-3 rounded-xl bg-gray-50 border border-gray-200 text-xs text-gray-600 leading-relaxed">
              <strong>Interpretation:</strong> An MGS of {mgs.score.toFixed(3)} places this variant
              in the <span style={{ color: scoreColor }}>{mgs.classification}</span> category,
              consistent with strong pathogenic evidence across all four dimensions.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
